import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { Product } from '../types';

interface WishlistItem {
  id: string;
  productId: string;
  variantId?: string;
  product?: Product;
}

interface WishlistContextType {
  items: WishlistItem[];
  loading: boolean;
  itemCount: number;
  addToWishlist: (productId: string, variantId?: string) => Promise<void>;
  removeFromWishlist: (itemId: string) => Promise<void>;
  isInWishlist: (productId: string, variantId?: string) => boolean;
  refreshWishlist: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshWishlist = async () => {
    try {
      const response = await api.get<WishlistItem[]>('/wishlist');
      setItems(response.data || []);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshWishlist();
  }, []);

  const addToWishlist = async (productId: string, variantId?: string) => {
    try {
      await api.post('/wishlist/items', { productId, variantId });
      await refreshWishlist();
    } catch (error) {
      console.error('Failed to add to wishlist:', error);
      throw error;
    }
  };

  const removeFromWishlist = async (itemId: string) => {
    try {
      await api.delete(`/wishlist/items/${itemId}`);
      await refreshWishlist();
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
      throw error;
    }
  };

  const isInWishlist = (productId: string, variantId?: string): boolean => {
    return items.some(item => 
      item.productId === productId && 
      (!variantId || item.variantId === variantId)
    );
  };

  return (
    <WishlistContext.Provider
      value={{
        items,
        loading,
        itemCount: items.length,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        refreshWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};


